import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Text } from '../ui/index.js';
import type { WebhookEvent } from '@goodbuild/shared';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { formatRelative } from '../lib/format.js';

export const Route = createFileRoute('/admin/webhooks')({ component: AdminWebhooks });

/**
 * The webhook log.
 *
 * Retrying is always safe: every handler is idempotent, so a replay of an event that already
 * ran changes nothing (`02-api-and-data.md` section 9.1).
 */
function AdminWebhooks() {
  const qc = useQueryClient();
  const { isAdmin } = useAccess();

  const { data, isLoading } = useQuery({
    queryKey: keys.admin.webhooks,
    queryFn: async () => (await api.get<{ events: WebhookEvent[] }>('/admin/webhooks')).events,
    enabled: isAdmin,
    refetchInterval: 30_000,
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/admin/webhooks/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.admin.webhooks }),
  });

  if (!isAdmin) return <EmptyState title={'You cannot do that.'} />;
  if (isLoading) return <Loading />;

  const rows = data ?? [];
  if (rows.length === 0) return <EmptyState title={'None'} />;

  return (
    <FocusZone focusKey="admin-webhooks" direction="column" className="gb-stack">
      {rows.map((event) => {
        const failed = event.processedAt === null && event.attempts > 0;
        return (
          <Card key={event.id} size="1">
            <div className="gb-seat">
              <Text size="2">{event.type}</Text>
              <Badge color={event.processedAt ? 'green' : failed ? 'danger' : 'amber'}>
                {event.processedAt ? 'done' : failed ? 'failed' : 'queued'}
              </Badge>
              <span className="gb-seat__spacer" />
              {event.error ? (
                <Text size="1" color="gray">
                  {event.error.slice(0, 120)}
                </Text>
              ) : null}
              <Text size="1" color="gray">
                {event.attempts} · {formatRelative(event.receivedAt)}
              </Text>
              {failed ? (
                <Focusable onSelect={() => retry.mutate(event.id)} selectLabel={'Retry'}>
                  <Button size="1" variant="surface">
                    {'Retry'}
                  </Button>
                </Focusable>
              ) : null}
            </div>
          </Card>
        );
      })}
    </FocusZone>
  );
}
